import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import Tooltip from '../../components/Tooltip';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { getEmojiItemByChar } from '../../utils/emoji';
import ReactionAvatar from './ReactionAvatar';
import * as styles from './ReactionAvatarStack.module.scss';

const ABSOLUTE_MAXIMUM = 80;

const REACTION_FRAGMENT = gql(`
  fragment ReactionAvatarStack_reaction on PublicReactionFields {
    id
    insertedAt
    ...ReactionAvatar_reaction
  }
`);

type ReactionFragment = DocumentType<typeof REACTION_FRAGMENT>;

interface ReactionAvatarStackItemContainerProps {
  children: React.ReactNode;
  className?: string;
}

export const ReactionAvatarStackItemContainer = ({
  children,
  className,
}: ReactionAvatarStackItemContainerProps) => {
  return (
    <div className={classNames(styles.avatarStackItemContainer, className)}>
      {children}
    </div>
  );
};

interface ReactEmojiTooltipProps {
  reactions: ReactionFragment[];
  currentReaction: ReactionFragment;
  userId: string;
}
const ReactEmojiTooltip = ({
  reactions,
  currentReaction,
  userId,
}: ReactEmojiTooltipProps) => {
  const name = getEmojiItemByChar(currentReaction.emoji)?.name;

  if (!name) return null;

  if (currentReaction.author.id === userId) {
    return (
      <>
        Remove reaction
        <br />
        <Tooltip.Muted>{name}</Tooltip.Muted>
      </>
    );
  }
  const hasUserAlsoReactedWithSameEmoji = reactions.some(
    (r) => r.author.id === userId && r.emoji === currentReaction.emoji,
  );
  if (hasUserAlsoReactedWithSameEmoji) {
    return (
      <>
        You&apos;ve both reacted with
        <br />
        <Tooltip.Muted>{name}</Tooltip.Muted>
      </>
    );
  }
  return (
    <>
      Also react with
      <br />
      <Tooltip.Muted>{name}</Tooltip.Muted>
    </>
  );
};

interface AvatarContainerProps {
  children: React.ReactNode;
  last: boolean;
  index: number;
  total: number;
}

const AvatarContainer = ({
  children,
  last,
  index,
  total,
}: AvatarContainerProps) => {
  return (
    <div
      className={classNames(styles.avatarContainer, {
        [styles.last]: last,
      })}
      style={{
        zIndex: total - index,
      }}
    >
      {children}
    </div>
  );
};

interface ReactionAvatarStackProps {
  reactions: ReactionFragment[];
  ring?: boolean;
  withinOverlay?: boolean;
  maxItemsShown?: number;
  additionalNode?: React.ReactNode;
  onEmojiClick?: (reaction: ReactionFragment, event: React.MouseEvent) => void;
  spacing?: 'tight' | 'loose';
  highlightedEmojis?: Set<string>;
  hideOverflowCount?: boolean;
  showAdditionalNode?: boolean;
  expanded?: boolean;
}

const ReactionAvatarStack = ({
  reactions,
  ring,
  withinOverlay,
  additionalNode,
  onEmojiClick,
  spacing = 'tight',
  maxItemsShown,
  highlightedEmojis,
  hideOverflowCount,
  showAdditionalNode,
  expanded = false,
}: ReactionAvatarStackProps) => {
  const shouldOverlapAdditionalNode = maxItemsShown === 3;
  const currentUser = useAppSelector((state) => state.session.user);

  const emojiPopularity = _.sortBy(_.entries(_.countBy(reactions, 'emoji')), 1)
    .toReversed()
    .map(([emoji]) => emoji);

  const sortedReactions = _.sortBy(reactions, (reaction) => [
    new Date(reaction.insertedAt).valueOf(),
    emojiPopularity.length - emojiPopularity.indexOf(reaction.emoji),
    reaction.author.id === currentUser.id ? 1 : 0,
    reaction.author.id,
  ]).toReversed();

  const slicedReactions = (() => {
    if (maxItemsShown != null && maxItemsShown > 1) {
      return sortedReactions.slice(
        0,
        maxItemsShown === 3 ? maxItemsShown - 1 : maxItemsShown - 2,
      );
    }

    if (maxItemsShown === 1) {
      return sortedReactions.slice(0, 1);
    }

    return sortedReactions.slice(0, ABSOLUTE_MAXIMUM);
  })();

  const overflowCount = reactions.length - (slicedReactions.length - 1);

  return (
    <div
      className={classNames(styles.container, {
        [styles.wrap]: maxItemsShown == null,
        [styles.withinOverlay]: withinOverlay,
        [styles.overlapAdditionalNode]: shouldOverlapAdditionalNode,
        [styles.loose]: spacing === 'loose',
      })}
    >
      {slicedReactions.map((reaction, i) => {
        const isLast = i === slicedReactions.length - 1;
        const isFromCurrentUser = reaction.author.id === currentUser.id;
        const isHighlighted = highlightedEmojis?.has(reaction.emoji);

        const avatar = (
          <ReactionAvatar
            ring={ring}
            reaction={reaction}
            active={isFromCurrentUser}
            highlighted={isHighlighted}
            withinOverlay={withinOverlay}
            tooltip={
              expanded && (
                <ReactEmojiTooltip
                  reactions={reactions}
                  currentReaction={reaction}
                  userId={currentUser.id}
                />
              )
            }
          />
        );

        return (
          <AvatarContainer
            last={isLast}
            index={i}
            total={slicedReactions.length}
            key={reaction.id}
          >
            {onEmojiClick ? (
              <Button onClick={(event) => onEmojiClick(reaction, event)}>
                {avatar}
              </Button>
            ) : (
              avatar
            )}
          </AvatarContainer>
        );
      })}

      {(maxItemsShown == null || maxItemsShown > 1) && (
        <div>
          {!hideOverflowCount && overflowCount > 1 && !showAdditionalNode ? (
            <ReactionAvatarStackItemContainer className={styles.counter}>
              {reactions.length}
            </ReactionAvatarStackItemContainer>
          ) : (
            <div
              className={classNames(styles.additionalNode, {
                [styles.visible]: showAdditionalNode,
              })}
            >
              {additionalNode}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReactionAvatarStack;
